/**
 * Shared client-side SSE (Server-Sent Events) frame parser for chat-streaming
 * responses (`text/event-stream`, `data: {...}\n\n` framing).
 *
 * Hand-rolled rather than the native `EventSource` because EventSource only
 * supports GET requests — our chat endpoints are POST (JSON body, and the
 * Playground call also carries an auth header), so a `fetch()` + manual
 * `ReadableStream` reader is the standard approach (same pattern the Vercel
 * AI SDK / OpenAI streaming clients use).
 */

export interface SseFrame {
  type: string;
  [key: string]: unknown;
}

/**
 * Reads `response.body` as a stream of `data: {...}\n\n` frames, invoking
 * `onEvent` with each parsed JSON frame as it arrives. Throwing inside
 * `onEvent` aborts the read and rejects this promise — the standard way for a
 * caller to treat a `{"type":"error",...}` frame as a hard failure.
 */
export async function consumeSseResponse(
  response: Response,
  onEvent: (frame: SseFrame) => void
): Promise<void> {
  if (!response.body) {
    throw new Error('Response has no readable body — streaming is not supported here');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');

    let sep: number;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);

      const dataLines = frame
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart());
      if (dataLines.length === 0) continue;

      try {
        onEvent(JSON.parse(dataLines.join('\n')));
      } catch (err) {
        // A frame that fails JSON.parse is malformed/partial — skip it rather
        // than abort the whole stream. A frame onEvent itself rejects on
        // (e.g. a `{"type":"error"}` frame) is a real failure and must propagate.
        if (err instanceof SyntaxError) continue;
        throw err;
      }
    }
  }
}
